import './style.css';
import { initThemeToggle } from './theme';

initThemeToggle();

// ---------------------------------------------------------------------------
// Mobile nav toggle
// ---------------------------------------------------------------------------
function initMobileNav(): void {
  const toggle = document.getElementById('nav-toggle');
  const panel = document.getElementById('mobile-nav');
  if (!toggle || !panel) return;

  toggle.addEventListener('click', () => {
    const isOpen = panel.classList.toggle('flex');
    panel.classList.toggle('hidden', !isOpen);
    toggle.setAttribute('aria-expanded', String(isOpen));
    toggle.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
  });

  panel.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', () => {
      panel.classList.add('hidden');
      panel.classList.remove('flex');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Open menu');
    });
  });
}

// ---------------------------------------------------------------------------
// Diagram connector draw-in on scroll into view (reduced-motion safe via CSS)
// ---------------------------------------------------------------------------
function initDiagramReveal(): void {
  const diagram = document.getElementById('hero-diagram');
  if (!diagram) return;

  const lines = diagram.querySelectorAll('.diagram-line');
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          lines.forEach((line, i) => {
            setTimeout(() => line.classList.add('is-visible'), i * 120);
          });
          observer.disconnect();
        }
      });
    },
    { threshold: 0.4 }
  );
  observer.observe(diagram);
}

// ---------------------------------------------------------------------------
// About stats: staggered reveal + count-up on scroll into view
// (reduced-motion safe - values are set instantly, no animation loop)
// ---------------------------------------------------------------------------
function initStatsCounter(): void {
  const container = document.getElementById('about-stats');
  if (!container) return;

  const items = container.querySelectorAll<HTMLElement>('.stat-item');
  const nums = container.querySelectorAll<HTMLElement>('.stat-num');
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const animateCount = (el: HTMLElement, delay: number): void => {
    const target = Number(el.dataset.countTo ?? '0');
    const suffix = el.dataset.suffix ?? '';

    if (prefersReducedMotion) {
      el.textContent = `${target}${suffix}`;
      return;
    }

    const duration = 1400;
    let start: number | null = null;

    const tick = (timestamp: number): void => {
      if (start === null) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = Math.round(target * eased);
      el.textContent = progress === 1 ? `${value}${suffix}` : String(value);
      if (progress < 1) requestAnimationFrame(tick);
    };

    setTimeout(() => requestAnimationFrame(tick), delay);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        items.forEach((item, i) => {
          setTimeout(() => item.classList.add('is-visible'), i * 120);
        });
        nums.forEach((num, i) => animateCount(num, i * 120));
        observer.disconnect();
      });
    },
    { threshold: 0.4 }
  );
  observer.observe(container);
}

// ---------------------------------------------------------------------------
// Delivery closer: typewriter reveal on scroll into view
// (reduced-motion safe - full text is set instantly, no animation loop)
// ---------------------------------------------------------------------------
function initTypewriter(): void {
  const el = document.getElementById('delivery-typewriter');
  const textEl = el?.querySelector<HTMLElement>('.typewriter-text');
  const cursorEl = el?.querySelector<HTMLElement>('.typewriter-cursor');
  if (!el || !textEl) return;

  const messages = (el.dataset.typewriterMessages ?? '')
    .split('|')
    .map((m) => m.trim())
    .filter(Boolean);
  if (!messages.length) return;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion) {
    textEl.textContent = messages[0];
    return;
  }

  const typeDelay = 32;
  const deleteDelay = 18;
  const holdFull = 2200;
  const holdEmpty = 500;

  const loop = (msgIndex: number, i: number, deleting: boolean): void => {
    const message = messages[msgIndex];
    textEl.textContent = message.slice(0, i);

    if (!deleting && i === message.length) {
      cursorEl?.classList.add('is-done');
      setTimeout(() => loop(msgIndex, i, true), holdFull);
      return;
    }
    if (deleting && i === 0) {
      cursorEl?.classList.remove('is-done');
      const nextMsgIndex = (msgIndex + 1) % messages.length;
      setTimeout(() => loop(nextMsgIndex, 0, false), holdEmpty);
      return;
    }

    cursorEl?.classList.remove('is-done');
    const next = deleting ? i - 1 : i + 1;
    setTimeout(() => loop(msgIndex, next, deleting), deleting ? deleteDelay : typeDelay);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        loop(0, 0, false);
      });
    },
    { threshold: 0.6 }
  );
  observer.observe(el);
}

// ---------------------------------------------------------------------------
// Scroll reveal: fade in each top-level section as it enters the viewport.
// Sections already above the fold at load are shown immediately.
// ---------------------------------------------------------------------------
function initScrollReveal(): void {
  const targets = document.querySelectorAll<HTMLElement>('main > section, footer');
  if (!targets.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
  );

  targets.forEach((target) => {
    target.classList.add('scroll-reveal');
    observer.observe(target);
  });
}

// ---------------------------------------------------------------------------
// Scroll spy: underline the top-nav link for whichever section the reader is
// currently over. Drives `aria-current="page"` on both the desktop and mobile
// nav links (the underline style keys off that attribute in style.css).
// ---------------------------------------------------------------------------
function initScrollSpy(): void {
  const links = Array.from(document.querySelectorAll<HTMLAnchorElement>('a.nav-link[href^="#"]'));
  if (!links.length) return;

  const targetIds = Array.from(
    new Set(links.map((link) => link.getAttribute('href')?.slice(1) ?? '').filter(Boolean))
  );

  // Sections in document order, so the topmost one wins when several are in
  // the trigger band at once.
  const sections = targetIds
    .map((id) => document.getElementById(id))
    .filter((el): el is HTMLElement => el !== null)
    .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
  if (!sections.length) return;

  const visible = new Set<string>();

  const setActive = (id: string | null): void => {
    links.forEach((link) => {
      if (link.getAttribute('href') === `#${id}`) {
        link.setAttribute('aria-current', 'page');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) visible.add(entry.target.id);
        else visible.delete(entry.target.id);
      });
      const active = sections.find((section) => visible.has(section.id));
      setActive(active ? active.id : null);
    },
    // A thin band roughly a third of the way down the viewport acts as the
    // "you are here" line.
    { rootMargin: '-30% 0px -60% 0px', threshold: 0 }
  );

  sections.forEach((section) => observer.observe(section));
}

// ---------------------------------------------------------------------------
// Contact form: client-side validation + async submission to the hyprsoft
// contact-form backend, protected by a Turnstile token and a honeypot field.
// ---------------------------------------------------------------------------
declare global {
  interface Window {
    turnstile?: {
      reset: (widgetIdOrContainer?: string | HTMLElement) => void;
    };
  }
}

function initContactForm(): void {
  const form = document.getElementById('contact-form') as HTMLFormElement | null;
  if (!form) return;

  const nameField = document.getElementById('field-name') as HTMLInputElement | null;
  const nameError = document.getElementById('field-name-error');
  const emailField = document.getElementById('field-email') as HTMLInputElement | null;
  const emailError = document.getElementById('field-email-error');
  const messageField = document.getElementById('field-message') as HTMLTextAreaElement | null;
  const messageError = document.getElementById('field-message-error');
  const status = document.getElementById('form-status');
  const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');

  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    const isValid = form.checkValidity();
    const nameValid = nameField?.validity.valid ?? true;
    const emailValid = emailField?.validity.valid ?? true;
    const messageValid = messageField?.validity.valid ?? true;
    nameError?.classList.toggle('hidden', nameValid);
    emailError?.classList.toggle('hidden', emailValid);
    messageError?.classList.toggle('hidden', messageValid);

    if (!isValid) {
      if (status) status.textContent = 'Please fill in the required fields.';
      return;
    }

    const data = new FormData(form);

    // Honeypot: silently drop bot submissions.
    const company = String(data.get('company') ?? '').trim();
    if (company !== '') return;

    const payload = {
      siteId: 'easc',
      name: String(data.get('name') ?? ''),
      email: String(data.get('email') ?? ''),
      message: String(data.get('message') ?? ''),
      'cf-turnstile-response': String(data.get('cf-turnstile-response') ?? ''),
    };

    if (submitButton) submitButton.disabled = true;
    if (status) status.textContent = 'Sending your message…';

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        form.reset();
        if (status)
          status.textContent = 'Thank you. We received your message and will be in touch shortly.';
      } else {
        if (status)
          status.textContent = 'Something went wrong. Please email connect@easc.io directly.';
      }
    } catch {
      if (status)
        status.textContent =
          'We could not send your message. Please email connect@easc.io directly.';
    } finally {
      if (submitButton) submitButton.disabled = false;
      window.turnstile?.reset();
    }
  });
}

// ---------------------------------------------------------------------------
// Hero rotator: four "Featured Perspective" slides, auto-advancing every 15s
// and user-navigable via prev/next, keyboard arrows, and a play/pause toggle.
// Matches the house pattern (initTypewriter / initStatsCounter): motion is
// gated on prefers-reduced-motion - reduced-motion visitors get working
// controls but no auto-advance and instant slide swaps.
// ---------------------------------------------------------------------------
function initHeroRotator(): void {
  const rotator = document.querySelector<HTMLElement>('[data-hero-rotator]');
  if (!rotator) return;

  const slides = Array.from(rotator.querySelectorAll<HTMLElement>('[data-hero-slide]'));
  if (slides.length < 2) return;

  const track = rotator.querySelector<HTMLElement>('[data-hero-track]');
  const counter = rotator.querySelector<HTMLElement>('[data-hero-counter]');
  const prevBtn = rotator.querySelector<HTMLButtonElement>('[data-hero-prev]');
  const nextBtn = rotator.querySelector<HTMLButtonElement>('[data-hero-next]');
  const playPauseBtn = rotator.querySelector<HTMLButtonElement>('[data-hero-playpause]');

  const progressSegs = Array.from(rotator.querySelectorAll<HTMLElement>('[data-hero-seg]'));

  const total = slides.length;
  const INTERVAL = 15000;
  const EXIT_MS = 320;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let current = 0;
  let exitTimer: number | undefined;
  // Auto-advance is a single self-rearming setTimeout, never an interval, so
  // the play/pause toggle can keep the unspent time.
  let advanceTimer: number | undefined;
  let countdownStart = 0; // performance.now() when the running countdown began
  let remaining = INTERVAL; // ms left on the current slide before it advances
  // Explicit play/pause toggle. This is the ONLY thing that suspends
  // rotation - there is deliberately no hover/focus pause, since that
  // desynced badly when mixed with manual navigation. A keyboard user
  // reading a slide's links is handled inside the advance callback instead.
  let userPaused = prefersReducedMotion;

  const pad = (n: number): string => String(n).padStart(2, '0');
  const fillOf = (i: number): HTMLElement | null =>
    progressSegs[i]?.querySelector<HTMLElement>('.hero-progress-fill') ?? null;

  // -- Progress bars ----------------------------------------------------
  // Paint the static state: past slides full, future slides empty, and the
  // current slide empty (a live countdown fills it) - or full when there is
  // no countdown to run (reduced motion).
  const paintProgress = (): void => {
    progressSegs.forEach((seg, i) => {
      seg.classList.toggle('is-done', i < current);
      seg.classList.toggle('is-active', i === current);
      const fill = fillOf(i);
      if (!fill) return;
      const full = i < current || (i === current && prefersReducedMotion);
      fill.style.transition = 'none';
      fill.style.transform = full ? 'scaleX(1)' : 'scaleX(0)';
    });
  };
  const animateActiveFill = (duration: number): void => {
    const fill = fillOf(current);
    if (!fill || prefersReducedMotion) return;
    const from = 1 - Math.max(duration, 0) / INTERVAL;
    fill.style.transition = 'none';
    fill.style.transform = `scaleX(${from})`;
    void fill.offsetWidth; // commit the start position before transitioning
    fill.style.transition = `transform ${Math.max(duration, 0)}ms linear`;
    fill.style.transform = 'scaleX(1)';
  };
  const freezeActiveFill = (): void => {
    const fill = fillOf(current);
    if (!fill) return;
    const t = getComputedStyle(fill).transform;
    const scale = t && t !== 'none' ? new DOMMatrixReadOnly(t).a : 0;
    fill.style.transition = 'none';
    fill.style.transform = `scaleX(${scale})`;
  };

  // -- Countdown ------------------------------------------------------
  const stopCountdown = (): void => {
    if (advanceTimer !== undefined) {
      window.clearTimeout(advanceTimer);
      advanceTimer = undefined;
    }
  };
  // Start (or restart) the current slide's countdown with `ms` left to run.
  const startCountdown = (ms: number): void => {
    stopCountdown();
    remaining = ms;
    if (prefersReducedMotion || userPaused) return;
    countdownStart = performance.now();
    animateActiveFill(ms);
    advanceTimer = window.setTimeout(() => {
      advanceTimer = undefined;
      // Don't pull a slide out from under a keyboard user reading its
      // content; wait another full interval and check again.
      const focused = document.activeElement;
      if (focused && focused !== track && track?.contains(focused)) {
        startCountdown(INTERVAL);
        return;
      }
      show(current + 1, 1);
    }, ms);
  };
  const pauseCountdown = (): void => {
    if (advanceTimer === undefined) return;
    remaining = Math.max(remaining - (performance.now() - countdownStart), 0);
    stopCountdown();
    freezeActiveFill();
  };

  // -- Slide change ------------------------------------------------
  // dir: +1 advancing, -1 going back - drives which way slides travel.
  // Every slide change (manual OR automatic) restarts the countdown and its
  // progress bar from a full interval.
  function show(index: number, dir = 1): void {
    const nextIndex = (index + total) % total;
    const outgoing = slides[current];
    const incoming = slides[nextIndex];
    current = nextIndex;

    track?.classList.toggle('is-back', dir < 0);

    if (outgoing !== incoming) {
      window.clearTimeout(exitTimer);
      slides.forEach((s) => {
        if (s !== incoming && s !== outgoing) s.classList.remove('is-exit');
      });
      outgoing.classList.remove('is-active');
      outgoing.classList.add('is-exit');
      exitTimer = window.setTimeout(() => outgoing.classList.remove('is-exit'), EXIT_MS);
      // Snap the incoming slide back to its resting offset, then force a
      // reflow so the browser animates the entry instead of jumping.
      incoming.classList.remove('is-exit');
      void incoming.offsetWidth;
    }
    incoming.classList.add('is-active');

    slides.forEach((slide, i) => {
      const active = i === current;
      slide.toggleAttribute('inert', !active);
      slide.setAttribute('aria-hidden', String(!active));
    });
    if (counter) counter.textContent = `${pad(current + 1)} / ${pad(total)}`;

    paintProgress();
    startCountdown(INTERVAL);
  }

  const goTo = (index: number, dir = 1): void => show(index, dir);

  const setPlayState = (paused: boolean): void => {
    userPaused = paused;
    if (playPauseBtn) {
      playPauseBtn.setAttribute('aria-pressed', String(paused));
      playPauseBtn.setAttribute(
        'aria-label',
        paused ? 'Resume automatic rotation' : 'Pause automatic rotation'
      );
    }
    if (paused) pauseCountdown();
    else startCountdown(remaining);
  };

  prevBtn?.addEventListener('click', () => goTo(current - 1, -1));
  nextBtn?.addEventListener('click', () => goTo(current + 1, 1));
  playPauseBtn?.addEventListener('click', () => setPlayState(!userPaused));
  progressSegs.forEach((seg, i) => {
    // Any segment click restarts the countdown - jumping to that slide, or
    // re-arming the current one.
    seg.addEventListener('click', () => goTo(i, i < current ? -1 : 1));
  });

  track?.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(current - 1, -1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(current + 1, 1);
    }
  });

  // Initial state.
  if (prefersReducedMotion) setPlayState(true);
  show(0);
}

// The hero 3D scene (three.js) is a heavy progressive enhancement - only
// fetch it for visitors who can actually use it, so everyone else gets the
// static SVG diagram without paying for an unused chunk.
function initHero3DIfSupported(): void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const canvas = document.createElement('canvas');
  const hasWebGL = !!(
    window.WebGLRenderingContext &&
    (canvas.getContext('webgl2') || canvas.getContext('webgl'))
  );
  if (!hasWebGL) return;

  import('./hero-3d').then(({ initHero3D }) => initHero3D());
}

initMobileNav();
initHeroRotator();
initScrollReveal();
initScrollSpy();
initDiagramReveal();
initHero3DIfSupported();
initStatsCounter();
initTypewriter();
initContactForm();
